import type {ReactNode} from 'react';
import styles from './ai-chat-quick-tips.module.scss';
import type {CapabilitiesSection, DoMoreSection, GeneralSection, LimitationsSection, QuickTipSection} from './ai-chat-quick-tips.types';
import {QUICK_TIP_SECTIONS, QuickTipSectionType} from './ai-chat-quick-tips.types';
import {AiChatQuickTipsItem} from './components/ai-chat-quick-tips-item/ai-chat-quick-tips-item';
import {AiChatQuickTipsCapabilities} from './components/ai-chat-quick-tips-capabilities/ai-chat-quick-tips-capabilities';
import {useAiChat} from '@/ai-chat/providers/ai-chat.context';
import {useAiChatSize} from '@AiChat/hooks/useAiChatSize';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Text, TextSize, TextTag, TextType} from '@Components/text';
import {Button, ButtonSize, ButtonType, ButtonVariant} from '@Components/button';
import {StaticDot} from '@Components/static-dot/static-dot';
import {StaticDotSize, StaticDotTheme} from '@Components/static-dot/static-dot.types';

export function AiChatQuickTips(): ReactNode {
  const {state, actions} = useAiChat();
  const {isSmall} = useAiChatSize();

  const renderGeneralSection = (section: GeneralSection, sectionIndex: number): ReactNode => (
    <section key={sectionIndex} className={'flex-v-row gap-3'}>
      <Text tag={TextTag.H3} size={TextSize.MEDIUM} bold className={styles.sectionHeading} val={window.i18next.t(section.headingKey)} />
      <div className={styles.itemsGroup}>
        {section.items.map((item) => (
          <AiChatQuickTipsItem
            key={item.titleKey}
            icon={item.icon}
            title={window.i18next.t(item.titleKey)}
            bulletPoints={item.bulletPointKeys.map((key) => window.i18next.t(key))}
            isSmall={isSmall}
          />
        ))}
      </div>
    </section>
  );

  const renderLimitationsSection = (section: LimitationsSection, sectionIndex: number): ReactNode => (
    <div key={sectionIndex} className={'flex-v-row gap-3'}>
      <Text size={TextSize.MEDIUM} bold val={window.i18next.t(section.headingKey)} />
      <div className={styles.itemsGroup}>
        {section.items.map((item) => (
          <AiChatQuickTipsItem
            key={item.titleKey}
            icon={item.icon}
            title={window.i18next.t(item.titleKey)}
            bulletPoints={item.bulletPointKeys.map((key) => window.i18next.t(key))}
            isSmall={isSmall}
          />
        ))}
      </div>
    </div>
  );

  const renderDoMoreSection = (section: DoMoreSection, sectionIndex: number): ReactNode => (
    <div key={sectionIndex} className={'flex-v-row gap-3'}>
      <Text size={TextSize.MEDIUM} bold val={window.i18next.t(section.headingKey)} />
      <div className={styles.bulletList}>
        {section.bulletPointKeys.map((bullet, bulletIndex) => (
          <div key={bulletIndex} className={styles.bulletItem}>
            <StaticDot size={StaticDotSize.SIZE_6} containerClassname={styles.bulletPointTopPaddingFixForWrappedText} theme={StaticDotTheme.DARK} />
            <Text val={window.i18next.t(bullet)} dangerouslySetInnerHTML type={TextType.BODY} size={TextSize.XSMALL} />
          </div>
        ))}
      </div>
    </div>
  );

  const renderCapabilitiesSection = (section: CapabilitiesSection, sectionIndex: number): ReactNode => (
    <AiChatQuickTipsCapabilities key={sectionIndex} headingKey={section.headingKey} capabilityGroups={state.capabilityGroups} isSmall={isSmall} />
  );

  const renderSection = (section: QuickTipSection, sectionIndex: number): ReactNode => {
    switch (section.type) {
      case QuickTipSectionType.GENERAL:
        return renderGeneralSection(section, sectionIndex);

      case QuickTipSectionType.LIMITATIONS:
        return renderLimitationsSection(section, sectionIndex);

      case QuickTipSectionType.DO_MORE:
        return renderDoMoreSection(section, sectionIndex);

      case QuickTipSectionType.CAPABILITIES:
        return renderCapabilitiesSection(section, sectionIndex);

      default:
        throw new Error(`Unhandled section type in renderSection in AiChatQuickTips`);
    }
  };

  const getHeader = (): ReactNode => {
    return (
      <div className={`gap-1 ${styles.header}`}>
        <div className={`_position-relative _full-height`}>
          <Icon
            icon="icon-arrow-left"
            size={isSmall ? IconSize.SIZE_ICON_20 : IconSize.SIZE_ICON_24}
            shape={IconShape.SQUARE}
            type={IconType.GHOST}
            isUsedAsButton
            onClick={actions.navigateBack}
          />
        </div>
        <div className={`spacing-p-t-1 ${styles.titleWrapper}`}>
          <h2 className={styles.title}>{window.i18next.t('pmwjs_ai_quick_tips_page_title')}</h2>
          <Icon icon="icon-sparkle-filled" size={IconSize.SIZE_ICON_20} shape={IconShape.NONE} className="content-ai spacing-m-l-2" />
        </div>
      </div>
    );
  };

  const getStartCreatingButton = (): ReactNode => (
    <Button
      type={ButtonType.PRIMARY} variant={ButtonVariant.AI}
      size={ButtonSize.MEDIUM}
      text={window.i18next.t('pmwjs_start_creating')}
      onClick={actions.startNewChat}
      customClasses={styles.startCreatingButton}
    />
  );

  const getContainerPaddingClasses = (): string => {
    if (isSmall) {
      return 'spacing-p-t-4 spacing-p-b-4 spacing-p-l-5 spacing-p-r-5';
    }

    return 'spacing-p-t-8 spacing-p-b-8 spacing-p-l-4 spacing-p-r-4';
  };

  return (
    <div className={`flex-v-row _full-height _full-width ${styles.container} ${getContainerPaddingClasses()}`}>
      <div className={`flex-v-row ${isSmall ? 'gap-4' : 'gap-8'} ${styles.content} ${isSmall ? styles.contentSmall : ''}`}>
        {getHeader()}
        <div className={'flex-v-row gap-8'}>{QUICK_TIP_SECTIONS.map(renderSection)}</div>
        {getStartCreatingButton()}
      </div>
    </div>
  );
}
